import { Component } from 'react';
import styled from 'styled-components';
import memoizeOne from 'memoize-one';
import Player from './Player';
import players from './players';
import { FilePlayer } from './players/FilePlayer';
import { defaultProps } from './props';
import type { BaseReactPlayerProps } from './types';
import { getConfig, isEqual } from './utils';

export default class ReactPlayer extends Component<BaseReactPlayerProps> {
  wrapper: HTMLDivElement | null = null;
  player: ReactPlayer | null = null;

  static defaultProps = defaultProps;

  static canPlay = (url: string) => {
    for (const player of players) {
      if (player.canPlay(url)) {
        return true;
      }
    }
    return false;
  };

  static canEnablePIP = (url: string) => {
    for (const player of players) {
      if (player.canEnablePIP && player.canEnablePIP(url)) {
        return true;
      }
    }
    return false;
  };

  config = getConfig(this.props, defaultProps);

  state = {
    showPreview: !!this.props.light
  };

  componentDidMount() {
    const { progressFrequency } = this.props;

    if (progressFrequency) {
      const message =
        'ReactPlayer: %cprogressFrequency%c is deprecated, please use %cprogressInterval%c instead';

      console.warn(message, 'font-weight: bold', '', 'font-weight: bold', '');
    }
  }

  shouldComponentUpdate(
    nextProps: BaseReactPlayerProps,
    nextState: BaseReactPlayerProps
  ) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  componentDidUpdate(prevProps: BaseReactPlayerProps) {
    const { light } = this.props;
    const { light: prevLight } = prevProps;
    this.config = getConfig(this.props, defaultProps);

    if (!prevLight && light) {
      this.setState({ showPreview: true });
    }

    if (prevLight && !light) {
      this.setState({ showPreview: false });
    }
  }

  showPreview = () => {
    this.setState({ showPreview: true });
  };

  getDuration = (): number | null => {
    if (!this.player) {
      return null;
    }

    return this.player.getDuration();
  };

  getCurrentTime = (): number | null => {
    if (!this.player) {
      return null;
    }

    return this.player.getCurrentTime();
  };

  getSecondsLoaded = (): number | null => {
    if (!this.player) {
      return null;
    }

    return this.player.getSecondsLoaded();
  };

  getInternalPlayer = (key = 'player'): Record<string, unknown> | null => {
    if (!this.player) {
      return null;
    }

    return this.player.getInternalPlayer(key);
  };

  seekTo = (fraction: number, type: 'seconds' | 'fraction'): void | null => {
    if (!this.player) {
      return null;
    }

    return this.player.seekTo(fraction, type);
  };

  getActivePlayer = memoizeOne((url: string) => {
    for (const { player, canPlay } of players) {
      if (canPlay(url)) {
        return player;
      }
    }
    // Fall back to FilePlayer if nothing else can play the URL
    return FilePlayer;
  });

  wrapperRef = (wrapper: HTMLDivElement) => {
    this.wrapper = wrapper;
  };

  playerRef = (player: ReactPlayer) => {
    this.player = player;
  };

  render() {
    const { url, style, width, height, hidden, onReady } = this.props;

    if (!url) {
      return null;
    }

    const activePlayer = this.getActivePlayer(url);

    if (!activePlayer) {
      return null;
    }

    return (
      <Wrapper
        ref={this.wrapperRef}
        hidden={hidden}
        _width={width}
        _height={height}
        style={{ ...style }}
      >
        <Player
          key={activePlayer.key}
          {...this.props}
          ref={this.playerRef}
          config={this.config}
          activePlayer={activePlayer}
          onReady={() => onReady?.(this)}
        />
      </Wrapper>
    );
  }
}

const Wrapper = styled.div<{
  _width?: number | string;
  _height?: number | string;
}>`
  opacity: 0;
  position: fixed;
  left: -1000%;
  ${({ _width }) => (_width ? _width : 'auto')};
  ${({ _height }) => (_height ? _height : 'auto')};
`;
